import React from 'react';
import { Language } from '../../pages/DevPortal';

export default function ChangelogSection({ lang }: { lang: Language }) {
    const isEn = lang === 'en';

    // Sacred numerology versions (3, 6, 9 or 8, 5, 2 etc) + 2 months shifted back
    // Mauricio requested: "solo recorre dos meses atras todo.. Jan 2026. Necesito que todas las vesiones tengan numerologia sagrada discretamente"
    
    // Numbers used:
    // 2.5.2 (Adds to 9) 
    // 2.3.4 (Adds to 9)
    // 2.1.6 (Adds to 9)
    // 1.8.9 (Adds to 18 -> 9)
    // 1.5.3 (Adds to 9)
    // 1.2.6 (Adds to 9)
    // 1.1.7 (Adds to 9)
    // 1.0.8 (Adds to 9)

    const releases = [
        {
            version: 'v2.5.2',
            date: isEn ? 'Jan 2026' : 'Ene 2026',
            title: isEn ? 'FHIR Readiness & Developer Portal' : 'Integración FHIR y Portal de Desarrolladores',
            items: [
                { tag: 'Feature', color: 'bg-emerald-100 text-emerald-800', desc: isEn ? 'Developer Documentation Portal with token-based access' : 'Portal de documentación con acceso por tokens' },
                { tag: 'Feature', color: 'bg-emerald-100 text-emerald-800', desc: isEn ? 'HL7 FHIR R4 data mapping for Patient, Encounter, Procedure' : 'Mapeo completo HL7 FHIR R4 para las entidades principales' },
                { tag: 'Security', color: 'bg-amber-100 text-amber-800', desc: isEn ? 'PIN hashing upgraded to SHA-256 via native Crypto API' : 'El hashing de PINs migra a SHA-256 (Web Crypto API)' }
            ]
        },
        {
            version: 'v2.3.4',
            date: isEn ? 'Dec 2025' : 'Dic 2025',
            title: isEn ? 'Zero Trust Staff Access' : 'Acceso Zero Trust Operativo',
            items: [
                { tag: 'Feature', color: 'bg-emerald-100 text-emerald-800', desc: isEn ? 'Individual staff authentication with 4-digit personal PINs' : 'Autenticación individual por staff con PIN personal' },
                { tag: 'Feature', color: 'bg-emerald-100 text-emerald-800', desc: isEn ? 'Cloudflare Zero Trust integration for enterprise admin SSO' : 'Integración con Cloudflare Zero Trust para SSO corporativo' },
                { tag: 'Improvement', color: 'bg-blue-100 text-blue-800', desc: isEn ? '5-tier RBAC model implementation' : 'Implementación del modelo RBAC de 5 niveles' }
            ]
        },
        {
            version: 'v2.1.6',
            date: isEn ? 'Oct 2025' : 'Oct 2025',
            title: isEn ? 'Smart Advertising System' : 'Sistema Inteligente de Publicidad',
            items: [
                { tag: 'Feature', color: 'bg-emerald-100 text-emerald-800', desc: isEn ? 'R2 media upload pipeline for ad assets (images + video)' : 'Pipeline de subida a R2 para assets (imágenes y video)' },
                { tag: 'Feature', color: 'bg-emerald-100 text-emerald-800', desc: isEn ? 'Ad scheduling: day-of-week rules and exact-time injection' : 'Programación de anuncios por día y hora exacta' },
                { tag: 'Improvement', color: 'bg-blue-100 text-blue-800', desc: isEn ? 'Hospital ad settings: mobile toggle, TV toggle, surgery-aware hiding' : 'Configuraciones granulares de anuncio por hospital' }
            ]
        },
        {
            version: 'v1.8.9',
            date: isEn ? 'Aug 2025' : 'Ago 2025',
            title: isEn ? 'Real-Time Push Notifications' : 'Notificaciones Push VAPID',
            items: [
                { tag: 'Feature', color: 'bg-emerald-100 text-emerald-800', desc: isEn ? 'Web Push notifications to families using VAPID protocol' : 'Notificaciones Web Push a familias vía VAPID' },
                { tag: 'Feature', color: 'bg-emerald-100 text-emerald-800', desc: isEn ? 'Milestone-based notification trigger system' : 'Sistema de triggers basado en hitos quirúrgicos' }
            ]
        },
        {
            version: 'v1.5.3',
            date: isEn ? 'Jun 2025' : 'Jun 2025',
            title: isEn ? 'Multi-Tenant Architecture' : 'Arquitectura Multi-Tenant y Analíticas',
            items: [
                { tag: 'Feature', color: 'bg-emerald-100 text-emerald-800', desc: isEn ? 'Hospital Groups: chain-level management for multi-hospital Orgs' : 'Gestión a nivel cadena para organizaciones hospitalarias' },
                { tag: 'Improvement', color: 'bg-blue-100 text-blue-800', desc: isEn ? 'Data isolation enforced at DB query level (scoped by hospitalId)' : 'Aislamiento de datos en queries de DB obligatorio' }
            ]
        },
        {
            version: 'v1.0.8',
            date: isEn ? 'Jan 2025' : 'Ene 2025',
            title: isEn ? 'Platform Launch' : 'Lanzamiento de Plataforma',
            items: [
                { tag: 'Feature', color: 'bg-emerald-100 text-emerald-800', desc: isEn ? 'Real-time surgical tracking for hospital waiting rooms' : 'Rastreo quirúrgico en tiempo real en Edge' },
                { tag: 'Infrastructure', color: 'bg-purple-100 text-purple-800', desc: isEn ? 'Cloudflare Workers & D1 edge deployment' : 'Despliegue distribuido en Cloudflare Workers y D1' }
            ]
        }
    ];

    return (
        <div className="animate-fade-in max-w-3xl">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
                {isEn ? 'Changelog' : 'Historial de Versiones'}
            </h1>
            <p className="text-lg text-slate-500 mb-12">
                {isEn ? 'Release history and platform updates' : 'Actualizaciones e historial de la plataforma'}
            </p>

            <div className="relative border-l border-slate-200 ml-3 space-y-12 pb-10">
                {releases.map((release, i) => (
                    <div key={i} className="relative pl-8">
                        {/* Timeline dot */}
                        <div className="absolute -left-1.5 top-1.5 w-3 h-3 bg-white border-2 border-indigo-500 rounded-full shadow-[0_0_0_4px_white]"></div>
                        
                        <div className="flex items-center gap-3 mb-2">
                            <span className="px-2.5 py-0.5 rounded-md bg-indigo-600 text-white text-xs font-bold tracking-widest font-mono shadow-sm">
                                {release.version}
                            </span>
                            <span className="text-sm font-mono text-slate-400 font-medium tracking-wide">
                                {release.date}
                            </span>
                        </div>
                        
                        <h2 className="text-xl font-bold text-slate-900 mb-4">{release.title}</h2>
                        
                        <ul className="space-y-3">
                            {release.items.map((item, j) => (
                                <li key={j} className="flex items-start gap-3">
                                    <span className={`${item.color} text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full mt-0.5 shrink-0`}>
                                        {item.tag}
                                    </span>
                                    <span className="text-sm text-slate-600 leading-relaxed">
                                        {item.desc}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
}
